<template>
  <Loader v-if="loader" />

  <div class="rid-sdd-container">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <h2 class="page-title">
          <i class="pi pi-credit-card mr-2"></i>
          RID SDD Allianz
        </h2>
        <p class="page-subtitle">Gestione mandati SDD e notifiche Allianz</p>
      </div>
    </div>

    <!-- Main Content with TabView -->
    <div class="card main-card">
      <TabView v-model:activeIndex="activeTabIndex" @tab-change="onTabChange" class="custom-tabview">

        <!-- TAB: RID INVIATI -->
        <TabPanel>
          <template #header>
            <div class="tab-header">
              <i class="pi pi-send mr-2"></i>
              <span>RID Inviati</span>
              <Badge :value="totalInviati" class="ml-2" severity="info" />
            </div>
          </template>

          <!-- Filtri Inviati -->
          <div class="fp">
            <div class="fp-body">
                <div class="fp-fields">
                  <div class="fp-field fp-field--daterange">
                    <label class="fp-label">Periodo</label>
                    <div class="fp-daterange">
                      <Calendar v-model="filtersCommon.dataFrom" dateFormat="dd/mm/yy" :showIcon="true" placeholder="Da" />
                      <span class="fp-daterange-sep">—</span>
                      <Calendar v-model="filtersCommon.dataTo" dateFormat="dd/mm/yy" :showIcon="true" placeholder="A" />
                    </div>
                  </div>
                  <div class="fp-field">
                    <label class="fp-label">Distinta</label>
                    <Dropdown
                      v-model="filenameCommon"
                      :options="distinteOptions"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Tutte le distinte"
                      class="w-full"
                      :loading="loadingDistinte"
                      :disabled="loadingDistinte"
                      showClear
                      @change="() => loadInviati(1)"
                    />
                  </div>
                  <div class="fp-field fp-field--search">
                    <label class="fp-label">Cerca</label>
                    <div class="p-inputgroup">
                      <InputText v-model="filtersCommon.search" placeholder="Fattura, cliente, IBAN, importo…" @keyup.enter="loadInviati" />
                      <Button icon="pi pi-search" @click="loadInviati" />
                    </div>
                  </div>
                  <div class="fp-field fp-field--payment">
                    <label class="fp-label">Pagamento</label>
                    <SelectButton
                      v-model="filtersCommon.onlyMissingPayment"
                      :options="paymentFilterOptions"
                      optionLabel="label"
                      optionValue="value"
                      class="payment-filter-toggle"
                      @change="() => loadInviati(1)"
                    />
                  </div>
                </div>
                <div class="fp-actions">
                  <Button label="Azzera" icon="pi pi-refresh" class="p-button-outlined p-button-secondary p-button-sm" @click="resetFiltersInviati" />
                  <Button label="Cerca" icon="pi pi-search" @click="loadInviati" />
                </div>
            </div>
          </div>

          <!-- Statistiche Inviati -->
          <div class="stats-section">
            <div class="grid">
              <div class="col-12 md:col-6">
                <div class="stat-card stat-total">
                  <div class="stat-icon">
                    <i class="pi pi-list"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-label">Totale Inviati</span>
                    <span class="stat-value">{{ totalInviati }}</span>
                  </div>
                </div>
              </div>
              <div class="col-12 md:col-6">
                <div class="stat-card stat-info">
                  <div class="stat-icon">
                    <i class="pi pi-euro"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-label">Importo Totale</span>
                    <span class="stat-value">{{ formatCurrency(importoTotaleInviati) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- DataTable Inviati -->
          <div class="table-section">
            <div class="table-toolbar">
              <div class="toolbar-left">
                <span class="table-info">{{ itemsInviati.length }} elementi</span>
              </div>
              <div class="toolbar-right">
                <Button label="Export CSV" icon="pi pi-download" class="p-button-outlined" @click="exportCSVInviati" />
              </div>
            </div>

            <DataTable ref="dtInviati" :value="itemsInviati" :paginator="true" :rows="rowsInviati"
              :rowsPerPageOptions="[10, 25, 50, 100]" v-model:filters="tableFiltersInviati" filterDisplay="menu"
              dataKey="id" responsiveLayout="scroll" stripedRows :loading="loadingInviati" :lazy="true"
              :totalRecords="totalInviati" @page="onPageInviati" @sort="onSortInviati" :sortField="sortInviatiField"
              :sortOrder="sortInviatiOrder" sortMode="single"
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="Mostra {first} a {last} di {totalRecords}" class="p-datatable-sm">
              <template #empty>
                <div class="empty-message">
                  <i class="pi pi-inbox"></i>
                  <span>Nessun RID trovato</span>
                </div>
              </template>

              <Column field="codFattura" header="Numero Fattura" :sortable="true" style="min-width: 150px">
                <template #body="{ data }">
                  <a :href="`${baseUrl}/ask/clienti/fatture/${data.codFattura}/dettagli`"
                    target="_blank" rel="noopener noreferrer" class="code-badge fattura-link">
                    {{ data.codFattura }}
                  </a>
                </template>
              </Column>
              <Column field="titolare" header="Cliente" :sortable="true" style="min-width: 160px">
                <template #body="{ data }">
                  <a :href="`${baseUrl}/ask/clienti/${data.codiceCliente}/dettagli`"
                    target="_blank" rel="noopener noreferrer" class="fattura-link">
                    {{ data.titolare }}
                  </a>
                </template>
              </Column>
              <Column field="iban" header="IBAN" :sortable="true" style="min-width: 200px">
                <template #body="{ data }">
                  <code class="iban-code">{{ data.iban }}</code>
                </template>
              </Column>
              <Column field="mandatoId" header="Mandato" :sortable="true" style="min-width: 120px">
                <template #body="{ data }">
                  <span class="mandato-badge">{{ data.mandatoId }}</span>
                </template>
              </Column>
              <Column field="importo" header="Importo" :sortable="true" style="width: 110px">
                <template #body="{ data }">
                  <span class="currency-value">{{ formatCurrency(data.importo) }}</span>
                </template>
              </Column>
              <Column field="valuta" header="Valuta" :sortable="true" style="width: 80px" />
              <Column field="dataInvio" header="Data Invio" :sortable="true" style="width: 110px">
                <template #body="{ data }">
                  {{ formatDate(data.dataInvio) }}
                </template>
              </Column>
              <Column header="Pagamento" style="width: 110px; text-align: center" :exportable="false">
                <template #body="{ data }">
                  <!-- Payment già esistente -->
                  <Badge v-if="data.paymentExists" value="Registrato" severity="success" class="payment-badge" />
                  <!-- Può creare -->
                  <Button
                    v-else-if="data.canCreatePayment"
                    label="Mancante"
                    icon="pi pi-euro"
                    class="p-button-sm p-button-warning"
                    @click="openPaymentModal(data, 'inviato')"
                    v-tooltip.top="'Registra pagamento SDD'"
                  />
                  <!-- Non disponibile -->
                  <span
                    v-else
                    class="payment-unavailable"
                    v-tooltip.top="getPaymentTooltip(data)"
                  >
                    <i class="pi pi-minus-circle mr-1"></i>Non disponibile
                  </span>
                </template>
              </Column>
              <Column header="Dettagli" style="width: 90px" :exportable="false">
                <template #body="{ data }">
                  <Button icon="pi pi-eye" class="p-button-rounded p-button-text p-button-info"
                    @click="openDettagliModal(data)" v-tooltip.top="'Visualizza dettagli'" />
                </template>
              </Column>
            </DataTable>
          </div>
        </TabPanel>

        <!-- TAB: STORNI RICEVUTI -->
        <TabPanel>
          <template #header>
            <div class="tab-header">
              <i class="pi pi-replay mr-2"></i>
              <span>Storni Ricevuti</span>
              <Badge :value="totalNotifiche" class="ml-2" severity="warning" />
            </div>
          </template>

          <!-- Filtri Storni -->
          <div class="fp">
            <div class="fp-body">
                <div class="fp-fields">
                  <div class="fp-field fp-field--daterange">
                    <label class="fp-label">Periodo</label>
                    <div class="fp-daterange">
                      <Calendar v-model="filtersCommon.dataFrom" dateFormat="dd/mm/yy" :showIcon="true" placeholder="Da" />
                      <span class="fp-daterange-sep">—</span>
                      <Calendar v-model="filtersCommon.dataTo" dateFormat="dd/mm/yy" :showIcon="true" placeholder="A" />
                    </div>
                  </div>
                  <div class="fp-field">
                    <label class="fp-label">Distinta</label>
                    <Dropdown
                      v-model="filenameCommon"
                      :options="distinteNotificheOptions"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Tutte le distinte"
                      class="w-full"
                      :loading="loadingDistinteNotifiche"
                      :disabled="loadingDistinteNotifiche"
                      showClear
                      @change="() => loadNotifiche(1)"
                    />
                  </div>
                  <div class="fp-field fp-field--search">
                    <label class="fp-label">Cerca</label>
                    <div class="p-inputgroup">
                      <InputText v-model="filtersCommon.search" placeholder="Fattura, cliente, IBAN, importo…" @keyup.enter="loadNotifiche" />
                      <Button icon="pi pi-search" @click="loadNotifiche" />
                    </div>
                  </div>
                  <div class="fp-field fp-field--payment">
                    <label class="fp-label">Pagamento</label>
                    <SelectButton
                      v-model="filtersCommon.onlyMissingPayment"
                      :options="paymentFilterOptions"
                      optionLabel="label"
                      optionValue="value"
                      class="payment-filter-toggle"
                      @change="() => loadNotifiche(1)"
                    />
                  </div>
                </div>
                <div class="fp-actions">
                  <Button label="Azzera" icon="pi pi-refresh" class="p-button-outlined p-button-secondary p-button-sm" @click="resetFiltersNotifiche" />
                  <Button label="Cerca" icon="pi pi-search" @click="loadNotifiche" />
                </div>
            </div>
          </div>

          <!-- Statistiche Storni -->
          <div class="stats-section">
            <div class="grid">
              <div class="col-12 md:col-6">
                <div class="stat-card stat-warning">
                  <div class="stat-icon">
                    <i class="pi pi-replay"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-label">Totale Storni</span>
                    <span class="stat-value">{{ totalNotifiche }}</span>
                  </div>
                </div>
              </div>
              <div class="col-12 md:col-6">
                <div class="stat-card stat-danger">
                  <div class="stat-icon">
                    <i class="pi pi-euro"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-label">Importo Totale Storni</span>
                    <span class="stat-value negative">{{ formatCurrency(importoTotaleNotifiche) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- DataTable Notifiche -->
          <div class="table-section">
            <div class="table-toolbar">
              <div class="toolbar-left">
                <span class="table-info">{{ itemsNotifiche.length }} elementi</span>
              </div>
              <div class="toolbar-right">
                <Button label="Export CSV" icon="pi pi-download" class="p-button-outlined"
                  @click="exportCSVNotifiche" />
              </div>
            </div>

            <DataTable ref="dtNotifiche" :value="itemsNotifiche" :paginator="true" :rows="rowsNotifiche"
              :rowsPerPageOptions="[10, 25, 50, 100]" v-model:filters="tableFiltersNotifiche" filterDisplay="menu"
              dataKey="id" responsiveLayout="scroll" stripedRows :loading="loadingNotifiche" :lazy="true"
              :totalRecords="totalNotifiche" @page="onPageNotifiche" @sort="onSortNotifiche"
              :sortField="sortNotificheField" :sortOrder="sortNotificheOrder" sortMode="single"
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="Mostra {first} a {last} di {totalRecords}" class="p-datatable-sm"
              :rowClass="getNotificaRowClass">
              <template #empty>
                <div class="empty-message">
                  <i class="pi pi-inbox"></i>
                  <span>Nessuna notifica trovata</span>
                </div>
              </template>

              <Column field="codFattura" header="Numero Fattura" :sortable="true" style="min-width: 140px">
                <template #body="{ data }">
                  <a :href="`${baseUrl}/ask/clienti/fatture/${data.codFattura}/dettagli`"
                    target="_blank" rel="noopener noreferrer" class="code-badge fattura-link">
                    {{ data.codFattura }}
                  </a>
                </template>
              </Column>
              <Column field="titolare" header="Cliente" :sortable="true" style="min-width: 180px">
                <template #body="{ data }">
                  <a :href="`${baseUrl}/ask/clienti/${data.codiceCliente}/dettagli`"
                    target="_blank" rel="noopener noreferrer" class="fattura-link">
                    {{ data.titolare }}
                  </a>
                </template>
              </Column>
              <Column field="iban" header="IBAN" :sortable="true" style="min-width: 220px">
                <template #body="{ data }">
                  <code class="iban-code">{{ data.iban }}</code>
                </template>
              </Column>
              <Column field="importo" header="Importo" :sortable="true" style="width: 120px">
                <template #body="{ data }">
                  <span class="currency-value" :class="{ 'negative': data.importo < 0 }">
                    {{ formatCurrency(data.importo) }}
                  </span>
                </template>
              </Column>
              <Column field="dataRicezione" header="Data Ricezione" :sortable="true" style="width: 130px">
                <template #body="{ data }">
                  {{ formatDate(data.dataRicezione) }}
                </template>
              </Column>
              <Column field="causale" header="Causale" :sortable="true" style="min-width: 250px">
                <template #body="{ data }">
                  <span class="causale-text" v-tooltip.top="parseCausale(data.causale)">{{ parseCausale(data.causale)
                    }}</span>
                </template>
              </Column>
              <Column header="Pagamento" style="width: 130px; text-align: center" :exportable="false">
                <template #body="{ data }">
                  <Badge v-if="data.paymentExists" value="Registrato" severity="success" class="payment-badge" />
                  <Button
                    v-else-if="data.canCreatePayment"
                    label="Mancante"
                    icon="pi pi-euro"
                    class="p-button-sm p-button-warning"
                    @click="openPaymentModal(data, 'notifica')"
                    v-tooltip.top="'Registra storno pagamento'"
                  />
                  <span
                    v-else
                    class="payment-unavailable"
                    v-tooltip.top="getPaymentTooltip(data)"
                  >
                    <i class="pi pi-minus-circle mr-1"></i>Non disponibile
                  </span>
                </template>
              </Column>
              <Column header="Dati Raw" style="width: 120px; text-align: center" :exportable="false">
                <template #body="{ data }">
                  <Button label="Raw" icon="pi pi-eye" class="p-button-sm p-button-outlined p-button-info"
                    :loading="data._rawLoading" @click="openNotificaRawModal(data)"
                    v-tooltip.top="'Visualizza dati raw'" />
                </template>
              </Column>
            </DataTable>
          </div>
        </TabPanel>

        <!-- TAB: FIRMA FILE -->
        <TabPanel v-if="canViewFirmaFile">
          <template #header>
            <div class="tab-header">
              <i class="pi pi-file mr-2"></i>
              <span>Firma File</span>
              <Badge :value="totalFiles" class="ml-2" severity="danger" />
            </div>
          </template>

          <!-- Filtri Firma File -->
          <div class="fp">
            <div class="fp-body">
                <div class="fp-fields">
                  <div class="fp-field">
                    <label class="fp-label">Stato File</label>
                    <Dropdown v-model="filtersFiles.status" :options="statusOptions" optionLabel="label" optionValue="value"
                      placeholder="Seleziona stato" class="w-full" @change="loadFiles" />
                  </div>
                  <div class="fp-field fp-field--daterange">
                    <label class="fp-label">Periodo Creazione</label>
                    <div class="fp-daterange">
                      <Calendar v-model="filtersFiles.dataFrom" dateFormat="dd/mm/yy" :showIcon="true" placeholder="Da" />
                      <span class="fp-daterange-sep">—</span>
                      <Calendar v-model="filtersFiles.dataTo" dateFormat="dd/mm/yy" :showIcon="true" placeholder="A" />
                    </div>
                  </div>
                  <div class="fp-field fp-field--search">
                    <label class="fp-label">Cerca</label>
                    <div class="p-inputgroup">
                      <InputText v-model="filtersFiles.search" placeholder="Nome file..." @keyup.enter="loadFiles" />
                      <Button icon="pi pi-search" @click="loadFiles" />
                    </div>
                  </div>
                </div>
                <div class="fp-actions fp-actions--between">
                  <Button label="Segnala IBAN Errato" icon="pi pi-exclamation-triangle"
                    class="p-button-warning p-button-sm" @click="openIbanModal" />
                  <div class="flex gap-2">
                    <Button label="Azzera" icon="pi pi-refresh" class="p-button-outlined p-button-secondary p-button-sm" @click="resetFiltersFiles" />
                    <Button label="Cerca" icon="pi pi-search" @click="loadFiles" />
                  </div>
                </div>
            </div>
          </div>

          <!-- DataTable Firma File -->
          <div class="table-section">
            <div class="table-toolbar">
              <div class="toolbar-left">
                <span class="table-info">{{ itemsFiles.length }} file trovati</span>
              </div>
              <div class="toolbar-right">
                <Button label="Export CSV" icon="pi pi-download" class="p-button-outlined" @click="exportCSVFiles" />
              </div>
            </div>

            <DataTable ref="dtFiles" :value="itemsFiles" :paginator="true" :rows="rowsFiles"
              :rowsPerPageOptions="[10, 25, 50, 100]" v-model:filters="tableFiltersFiles" filterDisplay="menu"
              dataKey="id" responsiveLayout="scroll" stripedRows :loading="loadingFiles" :lazy="true"
              :totalRecords="totalFiles" @page="onPageFiles" @sort="onSortFiles" :sortField="sortFilesField"
              :sortOrder="sortFilesOrder" sortMode="single"
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="Mostra {first} a {last} di {totalRecords}" class="p-datatable-sm">
              <template #empty>
                <div class="empty-message">
                  <i class="pi pi-folder-open"></i>
                  <span>Nessun file da firmare trovato</span>
                </div>
              </template>

              <Column field="id" header="ID" style="width: 80px" />
              <Column field="filename" header="Nome File" :sortable="true" style="min-width: 250px">
                <template #body="{ data }">
                  <div class="flex align-items-center">
                    <i class="pi pi-file-pdf mr-2 text-red-500" style="font-size: 1.2rem;"></i>
                    <span class="font-medium">{{ data.filename }}</span>
                  </div>
                </template>
              </Column>
              <Column field="createdAt" header="Data Creazione" :sortable="true" style="width: 150px">
                <template #body="{ data }">
                  {{ formatDateTime(data.createdAt) }}
                </template>
              </Column>
              <Column field="importo" header="Importo" :sortable="true" style="width: 130px">
                <template #body="{ data }">
                  <span class="currency-value">{{ formatCurrency(data.importo) }}</span>
                </template>
              </Column>
              <Column field="status" header="Stato" style="width: 150px">
                <template #body="{ data }">
                  <Badge :value="getStatusLabel(data.status)" :severity="getStatusSeverity(data.status)" />
                </template>
              </Column>
              <Column header="Azioni" style="width: 150px; text-align: center" :exportable="false">
                <template #body="{ data }">
                  <div class="flex justify-content-center gap-2">
                    <!-- Pulsante Elabora / Firma -->
                    <Button v-if="data.status === 'non_firmato'" icon="pi pi-check-circle"
                      class="p-button-rounded p-button-text p-button-success" v-tooltip.top="'Elabora File'"
                      :loading="data._signing" @click="openConfirmModal('sign', data)" />

                    <!-- Pulsante Segnala Errore -->
                    <Button v-if="data.status === 'non_firmato'" icon="pi pi-times-circle"
                      class="p-button-rounded p-button-text p-button-danger" v-tooltip.top="'Segnala Errore'"
                      :loading="data._erroring" @click="openConfirmModal('error', data)" />
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </TabPanel>
      </TabView>
    </div>

    <!-- Modal Dettagli RID -->
    <Dialog v-model:visible="showDettagliModal" :header="'Dettagli RID - ' + (selectedItem?.codFattura || '')"
      :modal="true" :style="{ width: '600px' }" :closable="true" class="dettagli-modal">
      <div v-if="selectedItem" class="dettagli-content">
        <!-- Informazioni Principali -->
        <div class="dettagli-section">
          <h4><i class="pi pi-info-circle mr-2"></i>Informazioni Principali</h4>
          <div class="dettagli-grid">
            <div class="dettagli-item">
              <span class="dettagli-label">Codice Cliente</span>
              <span class="dettagli-value">{{ selectedItem.codiceCliente }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">Titolare</span>
              <span class="dettagli-value">{{ selectedItem.titolare }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">IBAN</span>
              <span class="dettagli-value iban">{{ selectedItem.iban }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">Importo</span>
              <span class="dettagli-value currency">{{ formatCurrency(selectedItem.importo) }} {{ selectedItem.valuta
                }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">Data Invio</span>
              <span class="dettagli-value">{{ formatDate(selectedItem.dataInvio) }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">Mandato ID</span>
              <span class="dettagli-value">{{ selectedItem.mandatoId }}</span>
            </div>
            <div class="dettagli-item full-width">
              <span class="dettagli-label">Causale</span>
              <span class="dettagli-value">{{ selectedItem.causale }}</span>
            </div>
          </div>
        </div>

        <!-- Dettagli Tecnici -->
        <div v-if="selectedItem.dettagli" class="dettagli-section">
          <h4><i class="pi pi-cog mr-2"></i>Dettagli Tecnici</h4>
          <div class="dettagli-grid">
            <div class="dettagli-item">
              <span class="dettagli-label">Filename</span>
              <span class="dettagli-value code">{{ selectedItem.dettagli.filename }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">Instruction ID</span>
              <span class="dettagli-value code">{{ selectedItem.dettagli.instructionId }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">Purpose Code</span>
              <span class="dettagli-value">{{ selectedItem.dettagli.purposeCode }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">Data Firma Mandato</span>
              <span class="dettagli-value">{{ formatDate(selectedItem.dettagli.mandateSignatureDate) }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">Creato il</span>
              <span class="dettagli-value">{{ formatDateTime(selectedItem.dettagli.createdAt) }}</span>
            </div>
          </div>
        </div>

        <!-- Indirizzo Debitore -->
        <div v-if="selectedItem.dettagli && selectedItem.dettagli.debtorAddress" class="dettagli-section">
          <h4><i class="pi pi-map-marker mr-2"></i>Indirizzo Debitore</h4>
          <div class="dettagli-grid">
            <div class="dettagli-item">
              <span class="dettagli-label">Indirizzo</span>
              <span class="dettagli-value">{{ selectedItem.dettagli.debtorAddress }}</span>
            </div>
            <div class="dettagli-item">
              <span class="dettagli-label">Paese</span>
              <span class="dettagli-value">{{ selectedItem.dettagli.debtorCountry }}</span>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <Button label="Chiudi" icon="pi pi-times" class="p-button-text" @click="showDettagliModal = false" />
      </template>
    </Dialog>

    <!-- Modal Dati Raw Storno -->
    <Dialog v-model:visible="showRawModal"
      :header="'Dati raw storno - ' + (selectedRawNotifica?.codFattura || selectedRawNotifica?.id || '')" :modal="true"
      :style="{ width: '680px', maxWidth: '95vw' }" :closable="true" @hide="closeNotificaRawModal" class="raw-modal">
      <div class="raw-modal-content">
        <div v-if="selectedRawNotifica" class="raw-summary">
          <span>ID: {{ selectedRawNotifica.id }}</span>
          <span>Fattura: {{ selectedRawNotifica.codFattura || '-' }}</span>
          <span>Cliente: {{ selectedRawNotifica.codiceCliente || '-' }}</span>
        </div>

        <div v-if="rawLoading" class="raw-loading">
          <i class="pi pi-spin pi-spinner mr-2"></i>
          Caricamento dati raw in corso...
        </div>
        <div v-else-if="rawError" class="raw-error-message">
          <i class="pi pi-times-circle mr-2"></i>
          {{ rawError }}
        </div>
        <div v-else>
          <div class="raw-operator-guide">
            <div class="raw-guide-header">
              <div>
                <h4>Guida lettura storno</h4>
                <p>Codici e campi chiave per leggere subito il JSON.</p>
              </div>
              <Badge v-if="rawSummaryData.transactionStatus" :value="rawSummaryData.transactionStatus"
                :severity="getTransactionStatusSeverity(rawSummaryData.transactionStatus)" />
            </div>

            <div class="raw-code-tags">
              <div v-for="tag in rawCodeTags" :key="tag.key" class="raw-code-tag" :class="'raw-code-' + tag.severity">
                <span class="raw-code-field">{{ tag.field }}</span>
                <strong>{{ tag.value }}</strong>
                <span class="raw-code-label">{{ tag.label }}</span>
                <span v-if="tag.description" class="raw-code-description" v-tooltip.top="tag.description">
                  {{ tag.description }}
                </span>
              </div>
            </div>

            <div class="raw-key-values">
              <div v-for="item in rawOperatorFields" :key="item.label" class="raw-key-value">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </div>

          <div class="raw-json-header">
            <span>JSON raw completo</span>
            <small>Origine: raw.record</small>
          </div>
          <pre class="raw-json">{{ rawDataFormatted }}</pre>
        </div>
      </div>

      <template #footer>
        <Button label="Ricarica" icon="pi pi-refresh" class="p-button-outlined" :loading="rawLoading"
          :disabled="!selectedRawNotifica" @click="fetchNotificaRaw(selectedRawNotifica)" />
        <Button label="Chiudi" icon="pi pi-times" class="p-button-text" @click="closeNotificaRawModal" />
      </template>
    </Dialog>

    <!-- Modal Segnala IBAN Errato -->
    <Dialog v-model:visible="showIbanModal" header="Segnala IBAN Errato" :modal="true" :style="{ width: '500px' }"
      :closable="true" :dismissableMask="true" @hide="closeIbanModal" class="iban-modal">
      <div class="iban-modal-content">
        <p class="iban-info-text">
          Inserisci il numero fattura associata a questo file con IBAN errato.
          Il sistema aggiornerà automaticamente la modalità di pagamento a Bollettino
          e aprirà un ticket al backoffice per la richiamata al cliente.
        </p>
        <div class="field mt-3">
          <label class="filter-label">Numero Fattura <span class="text-red-500">*</span></label>
          <InputText v-model="ibanFattura" placeholder="es. A25006247" :maxlength="20" class="w-full"
            :disabled="ibanLoading" @keyup.enter="submitIbanErrato" />
        </div>
        <div v-if="ibanError" class="iban-error-message">
          <i class="pi pi-times-circle mr-2"></i>
          {{ ibanError }}
        </div>
      </div>
      <template #footer>
        <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="closeIbanModal"
          :disabled="ibanLoading" />
        <Button :label="ibanLoading ? 'Invio in corso...' : 'Conferma Segnalazione'" icon="pi pi-exclamation-triangle"
          class="p-button-warning" :loading="ibanLoading" :disabled="ibanLoading || !ibanFattura.trim()"
          @click="requestIbanConfirm" />
      </template>
    </Dialog>

    <!-- Modal Crea Pagamento -->
    <Dialog v-model:visible="showPaymentModal"
      :header="paymentSource === 'notifica' ? 'Conferma registrazione storno' : 'Conferma creazione pagamento'"
      :modal="true"
      :style="{ width: '500px' }" :closable="!paymentLoading" :dismissableMask="!paymentLoading"
      @hide="closePaymentModal" class="payment-modal">
      <div v-if="paymentItem" class="payment-modal-content">
        <i class="pi pi-euro payment-modal-icon"></i>
        <div class="payment-modal-text">
          <p v-if="paymentSource === 'notifica'">
            Stai per registrare lo <strong>storno</strong> di pagamento per la fattura
            <strong>{{ paymentItem.numeroFatturaEstratto || paymentItem.codFattura }}</strong>,
            intestata a <strong>{{ paymentItem.titolare }}</strong>,
            di importo <strong>{{ formatCurrency(paymentItem.importo) }}</strong>
            con data ricezione <strong>{{ formatDate(paymentItem.dataRicezione) }}</strong>.
          </p>
          <p v-else>
            Stai per registrare il pagamento SDD per la fattura
            <strong>{{ paymentItem.numeroFatturaEstratto }}</strong>,
            intestata a <strong>{{ paymentItem.titolare }}</strong>,
            di importo <strong>{{ formatCurrency(paymentItem.importo) }}</strong>
            con data addebito <strong>{{ formatDate(paymentItem.dataInvio) }}</strong>.
          </p>
          <p class="payment-modal-note">
            Vuoi procedere con la registrazione?
          </p>
        </div>
      </div>
      <template #footer>
        <Button label="Annulla" icon="pi pi-times" class="p-button-text" :disabled="paymentLoading"
          @click="closePaymentModal" />
        <Button label="Conferma" icon="pi pi-check" class="p-button-success" :loading="paymentLoading"
          @click="confirmCreatePayment" />
      </template>
    </Dialog>

    <!-- Modal Conferma Firma/Errore -->
    <Dialog v-model:visible="showConfirmModal" :header="confirmTitle" :modal="true" :style="{ width: '480px' }"
      :closable="false" class="confirm-modal">
      <div class="confirm-content">
        <i :class="confirmIcon" class="confirm-icon"></i>
        <div class="confirm-text">
          <p class="confirm-message">{{ confirmMessage }}</p>
          <p v-if="confirmDetails" class="confirm-details">{{ confirmDetails }}</p>
          <div v-if="confirmCooldown" class="confirm-wait">
            <span>Conferma disponibile tra {{ confirmCountdown }}s</span>
            <div class="confirm-progress">
              <div class="confirm-progress-bar" :style="{ width: confirmProgress + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="cancelConfirmModal" />
        <Button label="Conferma" icon="pi pi-check" class="p-button-primary" :disabled="confirmCooldown"
          @click="confirmAction" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, getCurrentInstance, computed, watch } from "vue";
import Loader from "@/components/Loader.vue";
import moment from "moment";

// PrimeVue Components
import Dropdown from "primevue/dropdown";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Calendar from "primevue/calendar";
import Badge from "primevue/badge";
import Dialog from "primevue/dialog";
import SelectButton from "primevue/selectbutton";
import { FilterMatchMode } from "primevue/api";

// Service per le chiamate API
import RidSddAllianzService from "@/services/ask-pratiche/rid-sdd-allianz-service";

export default {
  name: "RidSddAllianz",
  components: {
    Loader,
    TabView,
    TabPanel,
    DataTable,
    Column,
    Button,
    InputText,
    Calendar,
    Badge,
    Dialog,
    Dropdown,
    SelectButton,
  },

  setup() {
    const { proxy } = getCurrentInstance();

    // Service
    const service = new RidSddAllianzService(proxy.$http, proxy.$eventBus);

    // --- Permessi ---
    const _currentUser = JSON.parse(atob(localStorage.getItem('user'))).user[0];
    const canViewFirmaFile = computed(() => {
      const p = _currentUser?.profile;
      return !!(p && (p.all === 1 || p.contabilita === 1));
    });

    // --- State generale ---
    const loader = ref(false);
    const activeTabIndex = ref(canViewFirmaFile.value ? 2 : 0);
    const showDettagliModal = ref(false);
    const selectedItem = ref(null);
    const showConfirmModal = ref(false);
    const confirmActionType = ref(null);
    const confirmItem = ref(null);
    const confirmCooldown = ref(false);
    const confirmCountdown = ref(0);
    const confirmTimer = ref(null);
    const confirmDelaySeconds = 5;

    // --- RID Inviati ---
    const loadingInviati = ref(false);
    const dtInviati = ref(null);
    const itemsInviati = ref([]);
    const totalInviati = ref(0);
    const rowsInviati = ref(10);
    const pageInviati = ref(1);
    const importoTotaleInviati = ref(0);
    const sortInviatiField = ref(null);
    const sortInviatiOrder = ref(1);
    const filenameCommon = ref(null);
    const distinteOptions = ref([]);
    const loadingDistinte = ref(false);
    const distinteNotificheOptions = ref([]);
    const loadingDistinteNotifiche = ref(false);
    const filtersCommon = ref({
      dataFrom: null,
      dataTo: null,
      search: "",
      onlyMissingPayment: null,
    });
    const tableFiltersInviati = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    // --- Storni Ricevuti ---
    const loadingNotifiche = ref(false);
    const dtNotifiche = ref(null);
    const itemsNotifiche = ref([]);
    const totalNotifiche = ref(0);
    const rowsNotifiche = ref(10);
    const pageNotifiche = ref(1);
    const importoTotaleNotifiche = ref(0);
    const sortNotificheField = ref(null);
    const sortNotificheOrder = ref(1);
    const tableFiltersNotifiche = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const showRawModal = ref(false);
    const selectedRawNotifica = ref(null);
    const rawLoading = ref(false);
    const rawData = ref(null);
    const rawError = ref(null);

    // --- Segnala IBAN Errato ---
    const showIbanModal = ref(false);
    const ibanFattura = ref('');
    const ibanLoading = ref(false);
    const ibanError = ref(null);

    // --- Firma File ---
    const loadingFiles = ref(false);
    const dtFiles = ref(null);
    const itemsFiles = ref([]);
    const totalFiles = ref(0);
    const rowsFiles = ref(10);
    const pageFiles = ref(1);
    const sortFilesField = ref(null);
    const sortFilesOrder = ref(1);
    const statusOptions = [
      { label: 'Non Firmati', value: 'non_firmati' },
      { label: 'Firmati', value: 'firmati' },
      { label: 'Con Errori', value: 'errore' },
      { label: 'Tutti', value: 'all' },
    ];
    const filtersFiles = ref({
      dataFrom: null,
      dataTo: null,
      search: "",
      status: 'non_firmati',
    });
    const tableFiltersFiles = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    // =====================
    // Metodi - RID Inviati
    // =====================
    const loadInviati = async (page = 1, rows = rowsInviati.value) => {
      loadingInviati.value = true;
      try {
        const filters = {
          dataFrom: filtersCommon.value.dataFrom ? moment(filtersCommon.value.dataFrom).format("YYYY-MM-DD") : null,
          dataTo: filtersCommon.value.dataTo ? moment(filtersCommon.value.dataTo).format("YYYY-MM-DD") : null,
          search: filtersCommon.value.search,
          filename: filenameCommon.value || null,
          onlyMissingPayment: filtersCommon.value.onlyMissingPayment === true ? true : null,
        };
        const response = await service.getInviati(filters, page, rows);
        itemsInviati.value = response.items || [];
        applyLocalSort(itemsInviati, sortInviatiField.value, sortInviatiOrder.value);
        totalInviati.value = response.total || 0;
        importoTotaleInviati.value = response.totaleImporto || 0;
        pageInviati.value = page;
      } catch (error) {
        console.error("Errore nel caricamento dei RID inviati:", error);
        proxy.$toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Errore nel caricamento dei RID inviati",
          life: 3000,
        });
      } finally {
        loadingInviati.value = false;
      }
    };

    const onPageInviati = (event) => {
      rowsInviati.value = event.rows;
      loadInviati(Math.floor(event.first / event.rows) + 1, event.rows);
    };

    const resetFiltersInviati = () => {
      filtersCommon.value = { dataFrom: null, dataTo: null, search: "", onlyMissingPayment: null };
      filenameCommon.value = null;
      loadDistinte();
      loadInviati(1, rowsInviati.value);
    };

    const exportCSVInviati = () => {
      dtInviati.value.exportCSV();
    };

    const onSortInviati = (event) => {
      sortInviatiField.value = event.sortField;
      sortInviatiOrder.value = event.sortOrder;
      applyLocalSort(itemsInviati, sortInviatiField.value, sortInviatiOrder.value);
    };

    // ==========================
    // Metodi - Storni Ricevuti
    // ==========================
    const loadNotifiche = async (page = 1, rows = rowsNotifiche.value) => {
      loadingNotifiche.value = true;
      try {
        const filters = {
          dataFrom: filtersCommon.value.dataFrom ? moment(filtersCommon.value.dataFrom).format("YYYY-MM-DD") : null,
          dataTo: filtersCommon.value.dataTo ? moment(filtersCommon.value.dataTo).format("YYYY-MM-DD") : null,
          search: filtersCommon.value.search,
          filename: filenameCommon.value || null,
          onlyMissingPayment: filtersCommon.value.onlyMissingPayment === true ? true : null,
        };
        const response = await service.getNotifiche(filters, page, rows);
        itemsNotifiche.value = response.items || [];
        applyLocalSort(itemsNotifiche, sortNotificheField.value, sortNotificheOrder.value);
        totalNotifiche.value = response.total || 0;
        importoTotaleNotifiche.value = response.totaleImporto || 0;
        pageNotifiche.value = page;
        if (filenameCommon.value && totalNotifiche.value === 0) {
          proxy.$toast.add({
            severity: "info",
            summary: "Nessuno storno",
            detail: "Non esistono storni ricevuti per questa distinta.",
            life: 4000,
          });
        }
      } catch (error) {
        console.error("Errore nel caricamento degli storni:", error);
        proxy.$toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Errore nel caricamento degli storni",
          life: 3000,
        });
      } finally {
        loadingNotifiche.value = false;
      }
    };

    const onPageNotifiche = (event) => {
      rowsNotifiche.value = event.rows;
      loadNotifiche(Math.floor(event.first / event.rows) + 1, event.rows);
    };

    const resetFiltersNotifiche = () => {
      filtersCommon.value = { dataFrom: null, dataTo: null, search: "", onlyMissingPayment: null };
      filenameCommon.value = null;
      loadDistinteNotifiche();
      loadNotifiche(1, rowsNotifiche.value);
    };

    const exportCSVNotifiche = () => {
      dtNotifiche.value.exportCSV();
    };

    const onSortNotifiche = (event) => {
      sortNotificheField.value = event.sortField;
      sortNotificheOrder.value = event.sortOrder;
      applyLocalSort(itemsNotifiche, sortNotificheField.value, sortNotificheOrder.value);
    };

    const fetchNotificaRaw = async (item) => {
      if (!item?.id) return;
      rawLoading.value = true;
      rawError.value = null;
      item._rawLoading = true;
      try {
        rawData.value = await service.getNotificaRaw(item.id);
      } catch (error) {
        console.error("Errore nel caricamento dei dati raw:", error);
        rawData.value = null;
        rawError.value = "Errore nel caricamento dei dati raw dello storno selezionato.";
        proxy.$toast.add({
          severity: "error",
          summary: "Errore",
          detail: rawError.value,
          life: 4000,
        });
      } finally {
        rawLoading.value = false;
        item._rawLoading = false;
      }
    };

    const openNotificaRawModal = async (item) => {
      selectedRawNotifica.value = item;
      rawData.value = null;
      rawError.value = null;
      showRawModal.value = true;
      await fetchNotificaRaw(item);
    };

    const closeNotificaRawModal = () => {
      showRawModal.value = false;
      selectedRawNotifica.value = null;
      rawData.value = null;
      rawError.value = null;
      rawLoading.value = false;
    };

    // ======================
    // Metodi - Firma File
    // ======================
    const loadFiles = async (page = 1, rows = rowsFiles.value) => {
      loadingFiles.value = true;
      try {
        const filters = {
          dataFrom: filtersFiles.value.dataFrom ? moment(filtersFiles.value.dataFrom).format("YYYY-MM-DD") : null,
          dataTo: filtersFiles.value.dataTo ? moment(filtersFiles.value.dataTo).format("YYYY-MM-DD") : null,
          search: filtersFiles.value.search,
          status: filtersFiles.value.status,
        };
        const response = await service.getFilesFirma(filters, page, rows);
        itemsFiles.value = response.items || [];
        applyLocalSort(itemsFiles, sortFilesField.value, sortFilesOrder.value);
        totalFiles.value = response.total || 0;
        pageFiles.value = page;
      } catch (error) {
        console.error("Errore files:", error);
        proxy.$toast.add({ severity: "error", summary: "Errore", detail: "Errore caricamento file", life: 3000 });
      } finally {
        loadingFiles.value = false;
      }
    };

    const onPageFiles = (event) => {
      rowsFiles.value = event.rows;
      loadFiles(Math.floor(event.first / event.rows) + 1, event.rows);
    };

    const resetFiltersFiles = () => {
      filtersFiles.value = { dataFrom: null, dataTo: null, search: "", status: 'non_firmati' };
      loadFiles(1, rowsFiles.value);
    };

    const exportCSVFiles = () => {
      dtFiles.value.exportCSV();
    };

    const onSortFiles = (event) => {
      sortFilesField.value = event.sortField;
      sortFilesOrder.value = event.sortOrder;
      applyLocalSort(itemsFiles, sortFilesField.value, sortFilesOrder.value);
    };

    const getStatusSeverity = (status) => {
      if (status === 'firmato') return 'success';
      if (status === 'errore') return 'danger';
      return 'warning';
    };

    const getStatusLabel = (status) => {
      if (status === 'firmato') return 'Elaborato';
      if (status === 'errore') return 'Errore';
      return 'Da Elaborare';
    };

    const firmaFile = async (item) => {
      item._signing = true;
      try {
        await service.elaboraFile(item.id);
        proxy.$toast.add({ severity: 'success', summary: 'Successo', detail: 'File elaborato correttamente', life: 3000 });
        loadFiles(pageFiles.value, rowsFiles.value);
      } catch (error) {
        console.error('Errore elaborazione file:', error);
        proxy.$toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore durante l\'elaborazione del file', life: 3000 });
      } finally {
        item._signing = false;
      }
    };

    const segnalaErroreFile = async (item) => {
      item._erroring = true;
      try {
        await service.segnalaErroreFile(item.id);
        proxy.$toast.add({ severity: 'warn', summary: 'Segnalato', detail: 'File segnalato con errori', life: 3000 });
        loadFiles(pageFiles.value, rowsFiles.value);
      } catch (error) {
        console.error('Errore segnalazione file:', error);
        proxy.$toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore durante la segnalazione', life: 3000 });
      } finally {
        item._erroring = false;
      }
    };

    // ==========================
    // Metodi - Segnala IBAN Errato
    // ==========================
    const openIbanModal = () => {
      ibanFattura.value = '';
      ibanError.value = null;
      showIbanModal.value = true;
    };

    const closeIbanModal = () => {
      showIbanModal.value = false;
      ibanFattura.value = '';
      ibanError.value = null;
    };

    const requestIbanConfirm = () => {
      const fattura = ibanFattura.value.trim();
      if (!fattura) return;
      showIbanModal.value = false;
      openConfirmModal('iban_errato', { fattura });
    };

    const submitIbanErrato = async (fattura) => {
      if (!fattura) fattura = ibanFattura.value.trim();
      if (!fattura) return;
      ibanLoading.value = true;
      ibanError.value = null;
      try {
        await service.segnalaIbanErrato(fattura);
        ibanFattura.value = '';
        proxy.$toast.add({
          severity: 'success',
          summary: 'Segnalazione completata',
          detail: 'Fattura aggiornata e ticket creati.',
          life: 4000,
        });
      } catch (error) {
        const status = error?.response?.status;
        const code = error?.response?.data?.code;
        let errorMsg;
        if (status === 404) {
          errorMsg = 'Fattura non trovata. Verifica il numero inserito.';
        } else if (status === 422 && code === 'NOT_ALLIANZ_FATTURA') {
          errorMsg = 'Questo numero fattura non appartiene a una transazione Allianz.';
        } else {
          errorMsg = 'Errore durante la segnalazione. Riprova.';
        }
        ibanError.value = errorMsg;
        showIbanModal.value = true;
        proxy.$toast.add({ severity: 'error', summary: 'Errore', detail: errorMsg, life: 5000 });
      } finally {
        ibanLoading.value = false;
      }
    };

    // ======================
    // Metodi - Tab & Modali
    // ======================
    const onTabChange = (event) => {
      if (event.index === 0) {
        loadInviati(1, rowsInviati.value);
      } else if (event.index === 1) {
        loadNotifiche(1, rowsNotifiche.value);
      } else if (event.index === 2 && itemsFiles.value.length === 0) {
        loadFiles();
      }
    };

    const openDettagliModal = (item) => {
      selectedItem.value = item;
      showDettagliModal.value = true;
    };

    const openConfirmModal = (type, item) => {
      confirmActionType.value = type;
      confirmItem.value = item;
      showConfirmModal.value = true;
      startConfirmCooldown();
    };

    const closeConfirmModal = () => {
      showConfirmModal.value = false;
      confirmActionType.value = null;
      confirmItem.value = null;
      stopConfirmCooldown();
    };

    const cancelConfirmModal = () => {
      const wasIbanErrato = confirmActionType.value === 'iban_errato';
      closeConfirmModal();
      if (wasIbanErrato) {
        showIbanModal.value = true;
      }
    };

    const confirmAction = async () => {
      if (!confirmItem.value || !confirmActionType.value) return;
      const item = confirmItem.value;
      const actionType = confirmActionType.value;
      closeConfirmModal();
      if (actionType === "sign") {
        await firmaFile(item);
      } else if (actionType === "error") {
        await segnalaErroreFile(item);
      } else if (actionType === "iban_errato") {
        await submitIbanErrato(item.fattura);
      }
    };

    // ======================
    // Metodi - Formattazione
    // ======================
    const formatCurrency = (value) => {
      if (value == null) return "€ 0,00";
      return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(value);
    };

    const formatDate = (value) => {
      if (!value) return "-";
      return moment(value).format("DD/MM/YYYY");
    };

    const formatDateTime = (value) => {
      if (!value) return "-";
      return moment(value).format("DD/MM/YYYY HH:mm:ss");
    };

    const truncateText = (text, maxLength) => {
      if (!text) return "-";
      return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    };

    const parseCausale = (causale) => {
      if (!causale) return "-";
      return causale.split("|")[0].trim();
    };

    const getNotificaRowClass = () => "";

    const applyLocalSort = (itemsRef, field, order) => {
      if (!field || !itemsRef.value || itemsRef.value.length === 0) return;
      const sorted = [...itemsRef.value].sort((a, b) => {
        const valueA = a?.[field];
        const valueB = b?.[field];
        return compareValues(valueA, valueB);
      });
      itemsRef.value = order === -1 ? sorted.reverse() : sorted;
    };

    const isValidDate = (value) => {
      if (!value) return false;
      if (value instanceof Date && !isNaN(value.valueOf())) return true;
      return moment(value, moment.ISO_8601, true).isValid();
    };

    const normalizeValue = (value) => {
      if (value == null) return null;
      if (typeof value === "number") return value;
      if (typeof value === "string") return value.toLowerCase();
      if (isValidDate(value)) return moment(value).valueOf();
      return value;
    };

    const compareValues = (valueA, valueB) => {
      const normalizedA = normalizeValue(valueA);
      const normalizedB = normalizeValue(valueB);
      if (normalizedA == null && normalizedB == null) return 0;
      if (normalizedA == null) return -1;
      if (normalizedB == null) return 1;
      if (typeof normalizedA === "number" && typeof normalizedB === "number") {
        return normalizedA - normalizedB;
      }
      return String(normalizedA).localeCompare(String(normalizedB), "it", { numeric: true });
    };

    const startConfirmCooldown = () => {
      stopConfirmCooldown();
      confirmCooldown.value = true;
      confirmCountdown.value = confirmDelaySeconds;
      confirmTimer.value = setInterval(() => {
        if (confirmCountdown.value > 0) {
          confirmCountdown.value -= 1;
        }
        if (confirmCountdown.value <= 0) {
          confirmCooldown.value = false;
          stopConfirmCooldown();
        }
      }, 1000);
    };

    const stopConfirmCooldown = () => {
      if (confirmTimer.value) {
        clearInterval(confirmTimer.value);
        confirmTimer.value = null;
      }
    };

    const confirmTitle = computed(() => {
      if (confirmActionType.value === "iban_errato") return "Conferma segnalazione IBAN errato";
      return confirmActionType.value === "sign" ? "Conferma firma file" : "Conferma segnalazione errore";
    });

    const confirmMessage = computed(() => {
      if (confirmActionType.value === "iban_errato")
        return "Vuoi procedere con la segnalazione? La modalità di pagamento verrà aggiornata a Bollettino e verrà aperto un ticket al backoffice per la richiamata al cliente.";
      return confirmActionType.value === "sign"
        ? "Vuoi procedere con la firma del file?"
        : "Vuoi segnalare il file come errato?";
    });

    const confirmDetails = computed(() => {
      if (!confirmItem.value) return "";
      if (confirmActionType.value === "iban_errato")
        return `Numero fattura: ${confirmItem.value.fattura}`;
      return confirmItem.value.filename ? `File: ${confirmItem.value.filename}` : "";
    });

    const confirmIcon = computed(() => {
      if (confirmActionType.value === "iban_errato") return "pi pi-exclamation-triangle";
      return confirmActionType.value === "sign" ? "pi pi-check-circle" : "pi pi-times-circle";
    });

    const confirmProgress = computed(() => {
      if (!confirmCooldown.value) return 100;
      const elapsed = confirmDelaySeconds - confirmCountdown.value;
      return Math.min(100, Math.max(0, (elapsed / confirmDelaySeconds) * 100));
    });

    const rawSummaryData = computed(() => rawData.value?.summary || {});

    const rawRecordData = computed(() => rawData.value?.raw?.record || {});

    const getRawValue = (...keys) => {
      for (const key of keys) {
        const value = rawRecordData.value?.[key] ?? rawSummaryData.value?.[key];
        if (value !== undefined && value !== null && value !== "") return value;
      }
      return "-";
    };

    const getTransactionStatusSeverity = (status) => {
      if (status === "RJCT") return "danger";
      if (status === "ACCP" || status === "ACSC") return "success";
      if (status === "PDNG") return "warning";
      return "info";
    };

    const rawCodeTags = computed(() => [
      {
        key: "reason_code",
        field: "reason_code",
        label: "Codice motivo storno",
        value: getRawValue("reason_code", "reasonCode"),
        description: getRawValue("reason_description", "causale"),
        severity: "danger",
      },
      {
        key: "transaction_status",
        field: "transaction_status",
        label: "Stato transazione",
        value: getRawValue("transaction_status", "transactionStatus"),
        description: getRawValue("status_description"),
        severity: getTransactionStatusSeverity(getRawValue("transaction_status", "transactionStatus")),
      },
      {
        key: "status_id",
        field: "status_id",
        label: "ID stato flusso",
        value: getRawValue("status_id"),
        description: "Identificativo tecnico",
        severity: "info",
      },
      {
        key: "record_type",
        field: "record_type",
        label: "Tipo record",
        value: getRawValue("record_type"),
        description: "Categoria del record ricevuto",
        severity: "secondary",
      },
    ].filter((tag) => tag.value !== "-"));

    const rawOperatorFields = computed(() => [
      { label: "Fattura", value: getRawValue("end_to_end_id", "fattura") },
      { label: "Cliente", value: getRawValue("debtor_name", "cliente") },
      { label: "IBAN debitore", value: getRawValue("debtor_iban", "iban") },
      { label: "Importo storno", value: formatCurrency(rawSummaryData.value.importoStorno) },
      { label: "Importo originale", value: formatCurrency(rawSummaryData.value.importoOriginale ?? rawRecordData.value.original_amount) },
      { label: "Commissioni", value: formatCurrency(rawSummaryData.value.commissioni ?? rawRecordData.value.charge_amount) },
      { label: "File sorgente", value: getRawValue("file_name", "fileSorgente") },
      { label: "Data ricezione", value: getRawValue("creation_date", "dataRicezione") },
    ]);

    const activeFiltersCountCommon = computed(() => {
      let n = 0;
      if (filtersCommon.value.dataFrom) n++;
      if (filtersCommon.value.dataTo) n++;
      if (filtersCommon.value.search) n++;
      if (filtersCommon.value.onlyMissingPayment !== null) n++;
      return n;
    });
    const activeFiltersCountInviati = computed(() =>
      activeFiltersCountCommon.value + (filenameCommon.value ? 1 : 0)
    );
    const activeFiltersCountNotifiche = computed(() =>
      activeFiltersCountCommon.value + (filenameCommon.value ? 1 : 0)
    );
    const activeFiltersCountFiles = computed(() => {
      let n = 0;
      if (filtersFiles.value.dataFrom) n++;
      if (filtersFiles.value.dataTo) n++;
      if (filtersFiles.value.search) n++;
      if (filtersFiles.value.status !== 'non_firmati') n++;
      return n;
    });

    const rawDataFormatted = computed(() => {
      if (rawData.value == null) return "Nessun dato raw disponibile.";
      if (typeof rawData.value === "string") return rawData.value;
      return JSON.stringify(rawData.value, null, 2);
    });

    // ======================
    // Metodi - Crea Pagamento
    // ======================
    const showPaymentModal = ref(false);
    const paymentItem = ref(null);
    const paymentSource = ref("inviato"); // 'inviato' | 'notifica'
    const paymentLoading = ref(false);

    const paymentFilterOptions = [
      { label: "Tutti", value: null },
      { label: "Solo non registrati", value: true },
    ];

    const openPaymentModal = (item, source = "inviato") => {
      paymentItem.value = item;
      paymentSource.value = source;
      showPaymentModal.value = true;
    };

    const closePaymentModal = () => {
      showPaymentModal.value = false;
      paymentItem.value = null;
    };

    const getPaymentTooltip = (item) => {
      // Preferisci paymentReason fornito dal backend (se presente)
      if (item.paymentReason) return item.paymentReason;
      if (item.numeroFatturaEstratto === null) return "Numero fattura non estraibile dal record";
      if (item.dettagli?.firmato !== 1) return "Il file non è firmato";
      return "";
    };

    const confirmCreatePayment = async () => {
      if (!paymentItem.value) return;
      paymentLoading.value = true;
      try {
        const id = paymentItem.value.id;
        const res = paymentSource.value === "notifica"
          ? await service.createNotificaPayment(id)
          : await service.createPayment(id);
        const source = paymentSource.value;
        closePaymentModal();
        const isSkipped = res.status === "skipped_existing";
        proxy.$toast.add({
          severity: isSkipped ? "info" : "success",
          summary: isSkipped ? "Già esistente" : "Successo",
          detail: res.message,
          life: 4000,
        });
        if (source === "notifica") {
          loadNotifiche(pageNotifiche.value, rowsNotifiche.value);
        } else {
          loadInviati(pageInviati.value, rowsInviati.value);
        }
      } catch (error) {
        const status = error?.response?.status;
        const msg = error?.response?.data?.message || "Errore durante la creazione del pagamento.";
        proxy.$toast.add({
          severity: status >= 500 ? "error" : "warn",
          summary: status >= 500 ? "Errore" : "Operazione non consentita",
          detail: msg,
          life: 5000,
        });
      } finally {
        paymentLoading.value = false;
      }
    };

    // ======================
    // Metodi - Distinte
    // ======================
    const loadDistinte = async () => {
      loadingDistinte.value = true;
      const dataFrom = filtersCommon.value.dataFrom
        ? moment(filtersCommon.value.dataFrom).format("YYYY-MM-DD")
        : null;
      const dataTo = filtersCommon.value.dataTo
        ? moment(filtersCommon.value.dataTo).format("YYYY-MM-DD")
        : null;
      distinteOptions.value = await service.getDistinte(dataFrom, dataTo);
      if (filenameCommon.value && !distinteOptions.value.find(o => o.value === filenameCommon.value)) {
        filenameCommon.value = null;
      }
      loadingDistinte.value = false;
    };

    const loadDistinteNotifiche = async () => {
      loadingDistinteNotifiche.value = true;
      const dataFrom = filtersCommon.value.dataFrom
        ? moment(filtersCommon.value.dataFrom).format("YYYY-MM-DD")
        : null;
      const dataTo = filtersCommon.value.dataTo
        ? moment(filtersCommon.value.dataTo).format("YYYY-MM-DD")
        : null;
      distinteNotificheOptions.value = await service.getDistinteNotifiche(dataFrom, dataTo);
      if (filenameCommon.value && !distinteNotificheOptions.value.find(o => o.value === filenameCommon.value)) {
        filenameCommon.value = null;
      }
      loadingDistinteNotifiche.value = false;
    };

    watch(
      [() => filtersCommon.value.dataFrom, () => filtersCommon.value.dataTo],
      () => { loadDistinte(); loadDistinteNotifiche(); },
      { immediate: false }
    );

    // Lifecycle
    onMounted(() => {
      loadFiles();
      loadDistinte();
      loadDistinteNotifiche();
    });

    onBeforeUnmount(() => {
      stopConfirmCooldown();
    });

    const baseUrl = window.location.origin;

    return {
      baseUrl,

      // State generale
      loader,
      activeTabIndex,
      showDettagliModal,
      selectedItem,
      showConfirmModal,
      confirmCooldown,
      confirmCountdown,

      // RID Inviati
      loadingInviati,
      dtInviati,
      itemsInviati,
      totalInviati,
      rowsInviati,
      importoTotaleInviati,
      sortInviatiField,
      sortInviatiOrder,
      filtersCommon,
      filenameCommon,
      tableFiltersInviati,
      distinteOptions,
      loadingDistinte,
      loadInviati,
      onPageInviati,
      onSortInviati,
      resetFiltersInviati,
      exportCSVInviati,

      // Storni Ricevuti
      distinteNotificheOptions,
      loadingDistinteNotifiche,
      loadingNotifiche,
      dtNotifiche,
      itemsNotifiche,
      totalNotifiche,
      rowsNotifiche,
      importoTotaleNotifiche,
      sortNotificheField,
      sortNotificheOrder,
      tableFiltersNotifiche,
      showRawModal,
      selectedRawNotifica,
      rawLoading,
      rawError,
      rawSummaryData,
      rawCodeTags,
      rawOperatorFields,
      rawDataFormatted,
      loadNotifiche,
      onPageNotifiche,
      onSortNotifiche,
      resetFiltersNotifiche,
      exportCSVNotifiche,
      fetchNotificaRaw,
      openNotificaRawModal,
      closeNotificaRawModal,
      getTransactionStatusSeverity,

      // Firma File
      loadingFiles,
      dtFiles,
      itemsFiles,
      totalFiles,
      rowsFiles,
      sortFilesField,
      sortFilesOrder,
      statusOptions,
      filtersFiles,
      tableFiltersFiles,
      loadFiles,
      onPageFiles,
      onSortFiles,
      resetFiltersFiles,
      exportCSVFiles,
      getStatusSeverity,
      getStatusLabel,
      firmaFile,
      segnalaErroreFile,

      // Segnala IBAN Errato
      showIbanModal,
      ibanFattura,
      ibanLoading,
      ibanError,
      openIbanModal,
      closeIbanModal,
      submitIbanErrato,
      requestIbanConfirm,

      // Permessi
      canViewFirmaFile,

      // Tab & Modali
      onTabChange,
      openDettagliModal,
      openConfirmModal,
      closeConfirmModal,
      cancelConfirmModal,
      confirmAction,

      // Formattazione
      formatCurrency,
      formatDate,
      formatDateTime,
      truncateText,
      parseCausale,
      getNotificaRowClass,
      confirmTitle,
      confirmMessage,
      confirmDetails,
      confirmIcon,
      confirmProgress,

      // Crea Pagamento
      showPaymentModal,
      paymentItem,
      paymentSource,
      paymentLoading,
      paymentFilterOptions,
      openPaymentModal,
      closePaymentModal,
      confirmCreatePayment,
      getPaymentTooltip,

      activeFiltersCountInviati,
      activeFiltersCountNotifiche,
      activeFiltersCountFiles,
    };
  },
};
</script>

<style scoped>
.rid-sdd-container {
  padding: 0;
  width: 100%;
  max-width: 100%;
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-700) 100%);
  border-radius: 0;
  padding: 1.5rem 2.5rem;
  margin-bottom: 0;
  color: white;
}

.page-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.page-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 0.95rem;
}

/* Main Card */
.main-card {
  background: var(--surface-card);
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  min-height: calc(100vh - 120px);
}

/* Tab Header */
.tab-header {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
}

/* Custom TabView styling */
.custom-tabview :deep(.p-tabview-nav) {
  background: var(--surface-ground);
  border-radius: 0;
  padding: 0.5rem 0 0;
  display: flex;
}

.custom-tabview :deep(.p-tabview-nav li) {
  flex: 1;
}

.custom-tabview :deep(.p-tabview-nav li .p-tabview-nav-link) {
  justify-content: center;
  width: 100%;
}

.custom-tabview :deep(.p-tabview-panels) {
  padding: 2rem 2.5rem;
}

/* Filter Panel */
.fp {
  border-radius: 0;
  border: 1px solid var(--surface-border);
  margin-bottom: 1.5rem;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.fp-body {
  background: var(--surface-ground);
}

.fp-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem 1.1rem;
  padding: 1rem 1.1rem 0.85rem;
  align-items: flex-end;
}

.fp-field {
  display: flex;
  flex-direction: column;
  flex: 1 1 180px;
  min-width: 150px;
}

.fp-field--daterange {
  flex: 2 1 320px;
  min-width: 260px;
}

.fp-field--search {
  flex: 1.5 1 200px;
}

.fp-field--payment {
  flex: 1 1 180px;
}

.fp-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-color-secondary);
  margin-bottom: 0.4rem;
}

.fp-daterange {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.fp-daterange :deep(.p-calendar) {
  flex: 1;
  min-width: 0;
}

.fp-daterange-sep {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
  opacity: 0.4;
  flex-shrink: 0;
}

.fp-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.1rem;
  border-top: 1px solid var(--surface-border);
  background: var(--surface-card);
}

.fp-actions--between {
  justify-content: space-between;
}

/* Toggle filtro Pagamento (Tutti / Solo non registrati) */
.payment-filter-toggle {
  display: flex;
  width: 100%;
  gap: 0.35rem;
}
.payment-filter-toggle :deep(.p-button),
.payment-filter-toggle :deep(.p-button:first-child),
.payment-filter-toggle :deep(.p-button:last-child) {
  flex: 1 1 0;
  justify-content: center;
  padding: 0.35rem 0.4rem;
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
  border-radius: 20px !important;
}
.payment-filter-toggle :deep(.p-button .p-button-label) {
  font-weight: 500;
}

/* Statistics Section */
.stats-section {
  margin-bottom: 2rem;
  padding: 0 0.5rem;
}

.stat-card {
  display: flex;
  align-items: center;
  background: var(--surface-card);
  border-radius: 10px;
  padding: 1.25rem 1.5rem;
  border-left: 4px solid;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s, box-shadow 0.2s;
  margin: 0.25rem;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-total {
  border-left-color: var(--primary-color);
}

.stat-success {
  border-left-color: var(--green-500);
}

.stat-warning {
  border-left-color: var(--yellow-500);
}

.stat-danger {
  border-left-color: var(--red-500);
}

.stat-info {
  border-left-color: var(--blue-500);
}

.stat-secondary {
  border-left-color: var(--surface-500);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  background: var(--surface-ground);
}

.stat-icon i {
  font-size: 1.25rem;
  color: var(--text-color-secondary);
}

.stat-icon-sm {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  background: var(--surface-ground);
}

.stat-icon-sm i {
  font-size: 1rem;
  color: var(--text-color-secondary);
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-label {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.stat-value-sm {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-color);
}

/* Table Section */
.table-section {
  background: var(--surface-card);
  border-radius: 8px;
  padding: 1.5rem;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.5rem 1.25rem 0.5rem;
  border-bottom: 1px solid var(--surface-border);
  margin-bottom: 1.25rem;
}

.table-info {
  font-size: 0.9rem;
  color: var(--text-color-secondary);
}

/* Code badges */
.code-badge {
  font-family: 'Consolas', 'Monaco', monospace;
  background: var(--surface-ground);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
}

.fattura-link {
  color: var(--primary-color);
  text-decoration: underline;
}

.fattura-link:hover {
  color: var(--primary-700);
}

.payment-badge {
  font-size: 0.78rem;
}

.payment-unavailable {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.payment-modal-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.5rem 0;
}

.payment-modal-icon {
  font-size: 2rem;
  color: var(--green-500);
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.payment-modal-text p {
  margin: 0 0 0.75rem 0;
  line-height: 1.6;
}

.payment-modal-note {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.payment-modal-note code {
  background: var(--surface-ground);
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.82rem;
}

.iban-code {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.8rem;
  background: var(--surface-ground);
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
}

.currency-value {
  font-weight: 500;
}

.currency-value.negative {
  color: var(--red-500);
}

.causale-text {
  font-size: 0.9rem;
  color: var(--text-color-secondary);
}

/* Empty message */
.empty-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-color-secondary);
}

.empty-message i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* Row highlight for items to manage */
:deep(.row-highlight) {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

/* Mandato badge */
.mandato-badge {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.85rem;
  color: var(--primary-color);
  font-weight: 500;
}

/* Modal Dettagli Styles */
.dettagli-content {
  padding: 0.5rem 0;
}

.dettagli-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--surface-border);
}

.dettagli-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.dettagli-section h4 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
  display: flex;
  align-items: center;
}

.dettagli-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.dettagli-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.dettagli-item.full-width {
  grid-column: 1 / -1;
}

.dettagli-label {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dettagli-value {
  font-size: 0.95rem;
  color: var(--text-color);
}

.dettagli-value.iban {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.9rem;
  background: var(--surface-ground);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  width: fit-content;
}

.dettagli-value.currency {
  font-weight: 600;
  color: var(--primary-color);
}

.dettagli-value.code {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.85rem;
  background: var(--surface-ground);
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  width: fit-content;
}

/* Modal Dati Raw Storno */
.raw-modal-content {
  padding: 0.25rem 0;
}

.raw-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.65rem;
}

.raw-summary span {
  background: var(--surface-ground);
  border-radius: 999px;
  color: var(--text-color-secondary);
  font-size: 0.78rem;
  padding: 0.25rem 0.55rem;
}

.raw-operator-guide {
  background: var(--surface-ground);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
}

.raw-guide-header {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.raw-guide-header h4 {
  color: var(--primary-color);
  font-size: 0.92rem;
  margin: 0 0 0.1rem 0;
}

.raw-guide-header p {
  color: var(--text-color-secondary);
  font-size: 0.78rem;
  line-height: 1.25;
  margin: 0;
}

.raw-code-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 0.65rem;
}

.raw-code-tag {
  align-items: center;
  border: 1px solid;
  border-radius: 999px;
  display: inline-flex;
  gap: 0.35rem;
  max-width: 100%;
  min-height: 30px;
  padding: 0.25rem 0.55rem;
}

.raw-code-field {
  border-radius: 999px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.12rem 0.4rem;
  white-space: nowrap;
}

.raw-code-tag strong {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.88rem;
  white-space: nowrap;
}

.raw-code-label,
.raw-code-description {
  color: var(--text-color-secondary);
  font-size: 0.74rem;
}

.raw-code-label {
  white-space: nowrap;
}

.raw-code-description {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.raw-code-danger {
  background: var(--red-50, #fff5f5);
  border-color: var(--red-300, #f5a0a0);
}

.raw-code-danger .raw-code-field {
  background: var(--red-500, #e74c3c);
  color: #fff;
}

.raw-code-warning {
  background: var(--yellow-50, #fff9e6);
  border-color: var(--yellow-300, #f5d76e);
}

.raw-code-warning .raw-code-field {
  background: var(--yellow-500, #f1c40f);
  color: #2c2c2c;
}

.raw-code-success {
  background: var(--green-50, #f0fff4);
  border-color: var(--green-300, #9be7b0);
}

.raw-code-success .raw-code-field {
  background: var(--green-500, #27ae60);
  color: #fff;
}

.raw-code-info {
  background: var(--blue-50, #f0f7ff);
  border-color: var(--blue-300, #9ecff5);
}

.raw-code-info .raw-code-field {
  background: var(--blue-500, #3498db);
  color: #fff;
}

.raw-code-secondary {
  background: var(--surface-card);
  border-color: var(--surface-border);
}

.raw-code-secondary .raw-code-field {
  background: var(--surface-500);
  color: #fff;
}

.raw-key-values {
  display: grid;
  gap: 0.4rem;
  grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
}

.raw-key-value {
  background: var(--surface-card);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  min-width: 0;
  padding: 0.45rem 0.55rem;
}

.raw-key-value span {
  color: var(--text-color-secondary);
  font-size: 0.66rem;
  font-weight: 600;
  text-transform: uppercase;
}

.raw-key-value strong {
  color: var(--text-color);
  font-size: 0.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.raw-json-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0 0 0.35rem 0;
}

.raw-json-header span {
  color: var(--text-color);
  font-weight: 600;
}

.raw-json-header small {
  color: var(--text-color-secondary);
  font-family: 'Consolas', 'Monaco', monospace;
}

.raw-loading,
.raw-error-message {
  align-items: center;
  border-radius: 6px;
  display: flex;
  font-size: 0.9rem;
  padding: 0.75rem 1rem;
}

.raw-loading {
  background: var(--surface-ground);
  color: var(--text-color-secondary);
}

.raw-error-message {
  background: var(--red-50, #fff5f5);
  border: 1px solid var(--red-300, #f5a0a0);
  color: var(--red-600, #c0392b);
}

.raw-json {
  background: var(--surface-ground);
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  color: var(--text-color);
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.78rem;
  line-height: 1.35;
  margin: 0;
  max-height: 320px;
  overflow: auto;
  padding: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Confirm Modal */
.confirm-content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 0.5rem 0;
}

.confirm-icon {
  font-size: 2rem;
  color: var(--primary-color);
}

.confirm-message {
  margin: 0 0 0.25rem 0;
  font-weight: 600;
}

.confirm-details {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.confirm-wait {
  margin-top: 0.75rem;
  color: var(--text-color-secondary);
  font-size: 0.85rem;
}

.confirm-progress {
  margin-top: 0.4rem;
  width: 100%;
  height: 6px;
  background: var(--surface-ground);
  border-radius: 999px;
  overflow: hidden;
}

.confirm-progress-bar {
  height: 100%;
  background: var(--primary-color);
  transition: width 0.25s linear;
}

/* Segnala IBAN Errato */
.iban-modal-content {
  padding: 0.25rem 0;
}

.iban-info-text {
  margin: 0 0 0.5rem 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  background: var(--surface-ground);
  border-left: 3px solid var(--yellow-500);
  border-radius: 0 6px 6px 0;
  padding: 0.75rem 1rem;
}

.iban-error-message {
  margin-top: 0.75rem;
  padding: 0.6rem 0.85rem;
  background: var(--red-50, #fff5f5);
  border: 1px solid var(--red-300, #f5a0a0);
  border-radius: 6px;
  color: var(--red-600, #c0392b);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

/* Dialog styling */
:deep(.dettagli-modal .p-dialog-header) {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--surface-border);
}

:deep(.dettagli-modal .p-dialog-content) {
  padding: 1.5rem;
}

:deep(.dettagli-modal .p-dialog-footer) {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--surface-border);
}

:deep(.raw-modal .p-dialog-header) {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--surface-border);
}

:deep(.raw-modal .p-dialog-content) {
  padding: 1.5rem;
}

:deep(.raw-modal .p-dialog-footer) {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--surface-border);
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .page-header {
    padding: 1.25rem 1.5rem;
  }

  .page-title {
    font-size: 1.4rem;
  }

  .custom-tabview :deep(.p-tabview-panels) {
    padding: 1.5rem;
  }

  .custom-tabview :deep(.p-tabview-nav) {
    padding: 0.5rem 0 0;
  }

  .fp-fields {
    padding: 0.85rem 1rem 0.75rem;
    gap: 0.75rem;
  }

  .fp-actions {
    padding: 0.5rem 1rem;
  }

  .stat-card {
    padding: 1rem 1.25rem;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
  }

  .stat-value {
    font-size: 1.25rem;
  }

  .table-section {
    padding: 1rem;
  }

  .table-toolbar {
    flex-direction: column;
    gap: 0.75rem;
  }

  .dettagli-grid {
    grid-template-columns: 1fr;
  }
}
</style>
